import {
    formatToLocalDate,
    getDateTimeStringWithoutTimezone,
    getTimeOfDay,
    roundToTwoDecimalPlaces
} from '@nagel-utils';
import { InfoLotChip, TourPointConfig } from '../../models/planningPageTypes';
import {
    DRIVE_INSTRUCTION_SHIPMENT_CHIPS,
    DRIVE_INSTRUCTION_TOUR_POINT_CHIPS
} from '../configuration/consts/pickupPlanning';
import { getProductTypeValue } from '../utils/pickupPlanningUtils';

export const DATE_FILTER_FIELD_NAME = 'PlannedDepartureTime';

export abstract class AppChipStrategy {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    applyStrategy(_key: string, _value: unknown, _tourPointType?: TourPointConfig['type']): InfoLotChip[] {
        throw new Error('Method not implemented.');
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isApplicable(_key = '', _value?: unknown, _keysMap?: Record<string, string>): boolean {
        return false;
    }
};

export class TourPointDateChipStrategy extends AppChipStrategy {
    override applyStrategy(key: string, value: unknown): InfoLotChip[] {
        const dateChipsInfo = this.getTourPointDateChips(key, value);

        return dateChipsInfo ? dateChipsInfo as InfoLotChip[] : [];
    }

    override isApplicable(key: string): boolean {
        return [
            DRIVE_INSTRUCTION_TOUR_POINT_CHIPS.DELIVERY_DATE_FROM
        ].includes(key);
    }

    private getTourPointDateChips(
        mappedKey: string,
        value: unknown
    ): InfoLotChip[] | null {
        return [
            {
                name: mappedKey,
                label: value
                    ? formatToLocalDate(new Date(getDateTimeStringWithoutTimezone(value as string) || ''))
                    : '--/--/----'
            },
            {
                name: DRIVE_INSTRUCTION_TOUR_POINT_CHIPS.PLANNED_TIME,
                label: value
                    ? getTimeOfDay(new Date(getDateTimeStringWithoutTimezone(value as string) || ''))
                    : '--:--'
            }
        ];
    }
};

export class ShipmentDateChipStrategy extends AppChipStrategy {
    override applyStrategy(key: string, value: string | number | boolean | number[]): InfoLotChip[] {
        const dateChipsInfo = this.getShipmentDateChips(key, value);

        return dateChipsInfo ? dateChipsInfo as InfoLotChip[] : [];
    }

    override isApplicable(key: string): boolean {
        return [
            DRIVE_INSTRUCTION_SHIPMENT_CHIPS.FIXED_DELIVERY_DATE_FROM,
            DRIVE_INSTRUCTION_SHIPMENT_CHIPS.FIXED_DELIVERY_TIME_FROM
        ].includes(key);
    }

    private config = {
        [DRIVE_INSTRUCTION_SHIPMENT_CHIPS.FIXED_DELIVERY_DATE_FROM]: {
            chipName: DRIVE_INSTRUCTION_TOUR_POINT_CHIPS.DELIVERY_DATE_FROM,
            emptyLabel: '--/--/----',
            formatter: formatToLocalDate
        },
        [DRIVE_INSTRUCTION_SHIPMENT_CHIPS.FIXED_DELIVERY_TIME_FROM]: {
            chipName: DRIVE_INSTRUCTION_SHIPMENT_CHIPS.FIXED_DELIVERY_TIME_FROM,
            emptyLabel: '--:--',
            formatter: getTimeOfDay
        }
    };

    private getShipmentDateChips(
        mappedKey: string,
        value: unknown
    ): InfoLotChip[] | null {
        const dateConfig = this.config[mappedKey];

        if (!dateConfig) {
            return null;
        }

        const label = value
            ? dateConfig.formatter(new Date(getDateTimeStringWithoutTimezone(value as string) || ''))
            : dateConfig.emptyLabel;

        return [{
            name: dateConfig.chipName,
            label
        }];
    }
};

export class CommonChipStrategy extends AppChipStrategy {
    override applyStrategy(key: string, value: string | number | boolean | number[]): InfoLotChip[] {
        return [{
            name: key,
            label: this.getProcessedInfoChipValue(value, key),
        }];
    }

    override isApplicable(key: string, value: unknown, keysMap: Record<string, string>): boolean {
        return keysMap && Object.values(keysMap).includes(key);
    }

    private getProcessedInfoChipValue(value: string | number | boolean | number[], key: string): string {
        let result = value?.toString() || '';

        if (key === 'uniqueTrafficFlows') {
            result = Array.isArray(value)
                ? value.join(', ')
                : value?.toString() || '';
        }

        if (typeof value === 'number') {
            result = roundToTwoDecimalPlaces(value).toString();
        }

        return result;
    }
};

export class ProductGroupsChipStrategy extends AppChipStrategy {
    override applyStrategy(key: string, value: string[]): InfoLotChip[] {
        return [{
            name: key,
            multipleNames: this.getProductTypeInfoChips(value)
        }];
    }

    override isApplicable(key: string, value: unknown): boolean {
        return !!(key === 'productGroups'
            && Array.isArray(value)
            && value.length);
    }

    private getProductTypeInfoChips = (value: string[]): string[] => {
        return value.length
            ? value.map(item => getProductTypeValue(item))
            : [];
    };
};